import {
  EXPORT_TYPE_NAMES,
  ExportTypes,
  ExportJobTypes,
  ExportTransportTypes,
  ReportNames,
} from 'constants/exportTypes';
import { ExportType } from 'constants/exportTypes';

import React, { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { getUserInfo } from 'config/requests';
import { useReportExport } from 'hooks/queries/useReportExport';
import { useListController, SortPayload, useNotify } from 'react-admin';
import { ExportTypesButton } from 'shared/mui/Buttons/ExportTypesButton/ExportTypesButton';
import { Typography } from 'shared/mui/Typography';
import { ExportEditButton } from 'shared/react-admin/Export/ExportEditButton/ExportEditButton';
import { ListPage } from 'shared/react-admin/Pages';
import { ReportExportDialog } from 'shared/widgets/ReportExportDialog/ReportExportDialog';
import { OrderReportData } from 'types/embedded/orderReport';

import { EXPORT_FIELDS_ORDERS_REPORT } from './constants/exportFields';
import { OrdersReportListContent } from './OrdersReportListContent';

export const OrdersReportList: React.FC = () => {
  const {
    filterValues,
    isLoading: isListLoading,
    refetch,
  } = useListController<OrderReportData>({
    resource: 'orders/report',
    sort: {} as SortPayload,
    queryOptions: { enabled: false },
  });

  const [isOpenNotifyDialog, setIsOpenNotifyDialog] = useState(false);
  const [isSendReportOnEmail, setIsSendReportOnEmail] = useState(false);
  const notify = useNotify();
  const [exportType, setExportType] = useState<ExportType>(ExportTypes.Excel);

  const { data: user } = useQuery({ queryKey: ['me'], queryFn: getUserInfo });

  const isFiltersApplied = useMemo(() => Object.keys(filterValues).length > 0, [filterValues]);

  const { mutate: exportMutate, isLoading: isExportLoading } = useReportExport({
    filters: {
      ...filterValues,
      exportType,
      jobType: ExportJobTypes.Success_transaction,
      ...(isSendReportOnEmail && { notificationTransport: ExportTransportTypes.Email }),
    },
    preferenceKey: ReportNames.Orders_report,
    onSuccess: () => {
      notify(
        `Файл экспорта исполненных транзакций формируется ${isSendReportOnEmail ? 'и будет отправлен вам на почту' : ''}`,
        {
          type: 'info',
        },
      );
      setIsOpenNotifyDialog(false);
    },
    onError: (error) => {
      notify(error.data?.errors[0]?.title, { type: 'error' });
    },
  });

  useEffect(() => {
    if (isFiltersApplied) {
      refetch();
    }
  }, [filterValues]);

  return (
    <ListPage
      actions={[
        <ExportTypesButton
          disabled={!isFiltersApplied}
          exportType={exportType}
          key="export-button"
          onChangeType={setExportType}
          onClick={() => setIsOpenNotifyDialog(true)}
        />,
      ]}
      empty={false}
      headerTitle={{
        titleText: 'Отчёт по исполненным транзакциям',
        tooltipText:
          'Отчёт по исполненным транзакциям формирует сведения только по исполненным / успешным заказам (со статусом успех). Для получения информации по не исполненным транзакциям, перейдите в раздел “Заказы”',
      }}
      listBoxProps={{ sx: { maxHeight: '100%' } }}
      pagination={<></>}
      queryOptions={{ enabled: isFiltersApplied }}
      resource="orders/report"
      sort={{} as SortPayload}
    >
      <OrdersReportListContent
        isFiltersApplied={isFiltersApplied}
        setIsOpenNotifyDialog={setIsOpenNotifyDialog}
      />
      <ReportExportDialog
        exportType={EXPORT_TYPE_NAMES[exportType]}
        isLoading={isListLoading || isExportLoading}
        onChange={() => setIsSendReportOnEmail((prev) => !prev)}
        onClose={() => setIsOpenNotifyDialog(false)}
        onSubmit={exportMutate}
        open={isOpenNotifyDialog}
        sendOnEmail={isSendReportOnEmail}
        title={
          <>
            <Typography component="span" sx={{ verticalAlign: 'middle' }} variant="inherit">
              Экспорт отчёта
            </Typography>
            <ExportEditButton
              fields={EXPORT_FIELDS_ORDERS_REPORT}
              preferenceKey={ReportNames.Orders_report}
            />
          </>
        }
        userEmail={user?.corporateEmail || user?.email}
      />
    </ListPage>
  );
};
